import { useState, useEffect } from 'react';
import { dashboardAPI, usersAPI } from '../services/api';
import { fmt } from '../utils/format';
import Sidebar from '../components/layout/Sidebar';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';

export default function DashboardPage() {
  const [summary,    setSummary]    = useState(null);
  const [winRate,    setWinRate]    = useState([]);
  const [byRep,      setByRep]      = useState([]);
  const [alerts,     setAlerts]     = useState([]);
  const [pipeline,   setPipeline]   = useState([]);
  const [loading,    setLoading]    = useState(true);

  // Periodo por omision: lo que va del anio en curso. El Ejecutivo compara
  // contra el anio anterior; aqui interesa el corriente. El embudo y las
  // alertas NO dependen del periodo -- son estado de hoy -- y las dos bandas
  // de abajo lo dicen, para que nadie lea el margen como si fuera del mes.
  const hoy = new Date().toISOString().slice(0, 10);
  const [desde, setDesde] = useState(hoy.slice(0, 4) + '-01-01');
  const [hasta, setHasta] = useState(hoy);

  // Cadena vacia = Total AMS. El tablero abre con la empresa completa y se
  // acota desde el selector; filtrar por vendedor es la excepcion, no el
  // arranque. La grafica Revenue by Salesperson NO recibe el filtro: esa
  // grafica ES la comparacion entre vendedores y con uno solo no dice nada.
  const [vend, setVend] = useState('');
  const [sellers, setSellers] = useState([]);

  useEffect(() => {
    usersAPI.salespeople().then(r => setSellers(r.data)).catch(() => setSellers([]));
  }, []);

  useEffect(() => {
    let vivo = true;
    const v = vend || undefined;
    Promise.all([
      dashboardAPI.summary(desde, hasta, v),
      dashboardAPI.winRate(desde, hasta, v),
      dashboardAPI.bySalesperson(desde, hasta),
      dashboardAPI.alerts(v),
      dashboardAPI.pipeline(v),
    ]).then(([s, w, r, a, p]) => {
      if (!vivo) return;
      setSummary(s.data);
      setWinRate(w.data);
      setByRep(r.data);
      setAlerts(a.data);
      setPipeline(p.data);
    }).finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, [desde, hasta, vend]);

  if (loading) return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content"><div className="loading-center"><div className="spinner" /></div></div>
    </div>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
        <div style={{ color: 'var(--text-2)', marginBottom: 4 }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ color: p.color }}>
            {p.name}: {p.name.includes('Revenue') ? fmt.currency(p.value) : p.value}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content">
        <div className="page-header">
          <div><h2>Dashboard</h2><div className="page-subtitle">Sales overview</div></div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select className="form-select" style={{ width: 180 }}
                    value={vend} onChange={e => setVend(e.target.value)}>
              <option value="">Total AMS</option>
              {sellers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Period</span>
            <input type="date" className="form-input" style={{ width: 148 }}
                   value={desde} max={hasta} onChange={e => setDesde(e.target.value)} />
            <span style={{ color: 'var(--text-2)' }}>to</span>
            <input type="date" className="form-input" style={{ width: 148 }}
                   value={hasta} min={desde} onChange={e => setHasta(e.target.value)} />
          </div>
        </div>

        <div className="page-body">
          {/* Dos bandas a proposito. Antes estaba todo revuelto en una sola fila y
              el margen promedio se calculaba sobre TODA la historia sin decirlo,
              asi que a los seis meses iba a ser un numero que ya no se movia. */}
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 8 }}>Today</div>
          <div className="stat-grid" style={{ marginBottom: 20 }}>
            <div className="stat-card">
              <div className="stat-label">Open Pipeline</div>
              <div className="stat-value">{fmt.currency(summary?.pipeline_value, 0)}</div>
              <div className="stat-sub">{summary?.open_quotes} quotes in pipeline</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Low Price Alerts</div>
              <div className="stat-value" style={{ color: summary?.low_price_alerts > 0 ? 'var(--red)' : 'var(--green)' }}>
                {summary?.low_price_alerts}
              </div>
              <div className="stat-sub">Open lines below recommended</div>
            </div>
          </div>

          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 8 }}>Period {desde} to {hasta}</div>
          <div className="stat-grid" style={{ marginBottom: 24 }}>
            <div className="stat-card">
              <div className="stat-label">Won Revenue</div>
              <div className="stat-value" style={{ color: 'var(--green)' }}>{fmt.currency(summary?.won_revenue, 0)}</div>
              <div className="stat-sub">{summary?.won_quotes} deals closed</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Avg CM%</div>
              <div className="stat-value">{fmt.percent(summary?.avg_cm_pct)}</div>
              <div className="stat-sub">Contribution margin</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            {/* Win Rate Chart */}
            <div className="card">
              <div className="card-header"><span className="card-title">Win Rate by Month (period)</span></div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={winRate} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="month" tick={{ fill: 'var(--text-3)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'var(--text-3)', fontSize: 11 }} unit="%" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="win_rate_pct" name="Win Rate %"
                    stroke="var(--green)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* By Salesperson */}
            <div className="card">
              <div className="card-header"><span className="card-title">Revenue by Salesperson (period)</span></div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={byRep} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="salesperson" tick={{ fill: 'var(--text-3)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'var(--text-3)', fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="won_revenue" name="Won Revenue" fill="var(--accent)" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* Pipeline */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Open Pipeline</span>
                <span className="text-muted text-sm">Top 10</span>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Quote</th><th>Customer</th><th>Rep</th><th>Total</th></tr>
                  </thead>
                  <tbody>
                    {pipeline.slice(0, 10).map(q => (
                      <tr key={q.id}>
                        <td className="text-mono" style={{ fontSize: 12 }}>{q.quote_number}</td>
                        <td style={{ fontSize: 12 }}>{q.customer_name}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-3)' }}>{q.salesperson_name?.split(' ')[0]}</td>
                        <td className="text-mono" style={{ fontWeight: 600 }}>{fmt.currency(q.grand_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Low Price Alerts */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">🔴 Low Price Alerts</span>
                <span className="text-muted text-sm">{alerts.length} lines</span>
              </div>
              {alerts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--green)', fontSize: 13 }}>
                  ✅ All prices within range
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr><th>Quote</th><th>Model</th><th>% Below</th><th>Net Sales</th></tr>
                    </thead>
                    <tbody>
                      {alerts.slice(0, 8).map(a => (
                        <tr key={a.id}>
                          <td style={{ fontSize: 12 }}>{a.quote_number}</td>
                          <td><span className="badge badge-gray">{a.model_code}</span></td>
                          <td className="alert-low">−{a.pct_below_rec}%</td>
                          <td className="text-mono" style={{ fontSize: 12 }}>{fmt.currency(a.net_sales)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
